import {
  SIDES_MAX,
  SIDES_MIN,
  SIDES_PRESET_VALUES,
  SIDES_RANGE_ERROR,
  isSidesEligibleShape,
  normSides,
  parseSidesCount,
} from '@/lib/object-effects/sides'
import { buildSidesHistoryItem, buildSidesLiveUpdate } from '../helpers/polygon-effect-updates'
import { useGeometryEffectControl } from '../hooks/use-geometry-effect-control'
import type { EffectControlProps } from '../types/effect-control-props'
import type { GeometryEffect } from '../types/geometry-effect'
import { EffectSliderField } from './effect-slider-field'

const SIDES_PRESETS = SIDES_PRESET_VALUES.map((v) => ({ label: String(v), value: v }))

function parseSidesInput(raw: string): number | null {
  const parsed = parseSidesCount(raw)
  const v = parsed.ok ? parsed.value : NaN

  if (!parsed.ok || v < SIDES_MIN || v > SIDES_MAX) return null
  return v
}

const SIDES_EFFECT: GeometryEffect = {
  restValue: SIDES_MIN,
  min: SIDES_MIN,
  max: SIDES_MAX,
  presets: SIDES_PRESETS,
  rangeError: SIDES_RANGE_ERROR,
  isEligible: isSidesEligibleShape,
  readValue: normSides,
  normalize: (value) => Math.max(SIDES_MIN, Math.min(SIDES_MAX, Math.round(value))),
  parseInput: parseSidesInput,
  liveUpdate: buildSidesLiveUpdate,
  historyItem: buildSidesHistoryItem,
  startSource: 'live',
}

/**
 * Sides 3–100 for eligible polygon / star shapes; `RegularPolygonElement.sides` on save.
 * Presets, live slider, commit-on-blur text, inline errors, multi-select, ineligible disabled —
 * the field behavior is shared with Inset / Rounding via `use-geometry-effect-control`.
 */
export function EffectsPanelSides(props: EffectControlProps) {
  const field = useGeometryEffectControl(SIDES_EFFECT, props)

  return (
    <EffectSliderField
      {...field}
      label="Sides"
      ariaLabel="Sides count"
      errorId="sides-input-error"
    />
  )
}
